// File: src/features/auth/components/AuthForms.js
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Lock, User, Building, Phone, CheckCircle, AlertCircle } from 'lucide-react';
// Common components
import Input from '../../../components/common/Input/Input';
import Button from '../../../components/common/Button/Button';
import CloudflareWidget from '../../../components/common/Cloudflare/CloudflareWidget';
import Autocomplete from '../../../components/common/Autocomplete/Autocomplete';
import Dropdown from '../../../components/common/Dropdown/Dropdown';
// Auth service
import { authService, mockAuthService } from '../services/authService';
// Location service
import locationService from '../services/locationService';
import PolicyModal from '../../../components/common/Modal/PolicyModal';
import { POLICY_CONTENT } from './policyData';
import './styles.css'

const anim = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -20 }
};

// Email validation helper
const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Password validation helper - enforces strong password requirements
const validatePassword = (password) => {
  const errors = [];

  if (!password) {
    errors.push('Password is required');
    return errors;
  }

  // Check minimum length
  if (password.length < 8) {
    errors.push('Must be at least 8 characters long');
  }

  // Check for uppercase letter
  if (!/[A-Z]/.test(password)) {
    errors.push('Must contain an uppercase letter (A-Z)');
  }

  // Check for lowercase letter
  if (!/[a-z]/.test(password)) {
    errors.push('Must contain a lowercase letter (a-z)');
  }

  // Check for number
  if (!/\d/.test(password)) {
    errors.push('Must contain a number (0-9)');
  }

  // Check for special character
  if (!/[!%@#$^&*()_\-+=\[\]{};':"\\|,.<>?/`~]/.test(password)) {
    errors.push('Must contain a special character (!, %, @, #, etc.)');
  }

  return errors;
};

// --- SYSTEM ALERT COMPONENT ---
const SystemAlert = ({ type = 'info', message, onClose }) => {
  if (!message) return null;

  const bgColor = {
    error: 'rgba(239, 68, 68, 0.1)',
    success: 'rgba(34, 197, 94, 0.1)',
    info: 'rgba(59, 130, 246, 0.1)',
    warning: 'rgba(245, 158, 11, 0.1)'
  }[type];

  const textColor = {
    error: '#ef4444',
    success: '#22c55e',
    info: '#3b82f6',
    warning: '#f59e0b'
  }[type];

  const IconComponent = type === 'error' ? AlertCircle : CheckCircle;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      style={{
        background: bgColor,
        border: `1px solid ${textColor}`,
        padding: '12px 16px',
        borderRadius: '6px',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: textColor,
        fontSize: '14px'
      }}
    >
      <IconComponent size={18} />
      <span style={{ flex: 1 }}>{message}</span>
      {onClose && (
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'inherit',
            cursor: 'pointer',
            padding: '0',
            fontSize: '18px'
          }}
        >
          ×
        </button>
      )}
    </motion.div>
  );
};

// --- LOGIN ---
export const LoginForm = ({ setView }) => {
  const [email, setEmail] = useState(localStorage.getItem('rememberedEmail') || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(!!localStorage.getItem('rememberedEmail'));
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState(null);

  // Policy Modal State
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyType, setPolicyType] = useState(null); // 'terms', 'privacy', 'cookie'

  const handlePolicyClick = (e, type) => {
    e.preventDefault();
    setPolicyType(type);
    setShowPolicyModal(true);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      console.log('=== LOGIN ATTEMPT ===');
      console.log('Email:', email);

      const response = await authService.login(email, password);
      console.log('Login response:', response);

      // Store remember me preference
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }

      setAlert({ type: 'success', message: response.message || 'Login successful! Sending OTP...' });
      setTimeout(() => {
        setView('otp', email);
      }, 1500);
    } catch (error) {
      console.error('=== LOGIN ERROR ===');
      console.error('Error:', error);
      console.error('Error response:', error.response?.data);

      let errorMessage = 'Login failed. Please check your credentials.';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setAlert({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div variants={anim} initial="hidden" animate="visible" exit="exit">
      <PolicyModal
        isOpen={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        title={policyType ? POLICY_CONTENT[policyType].title : ""}
        content={policyType ? POLICY_CONTENT[policyType].content : null}
      />
      {alert && (
        <SystemAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <form onSubmit={handlePasswordLogin}>
        <div className='mb-15'>
        {/* Email Input */}
        <Input
          icon={Mail}
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errors.email) setErrors({ ...errors, email: '' });
          }}
          error={errors.email}
          autoComplete="username"
          disabled={loading}
          aria-label="Email address"
        />
</div>
<div className='mb-15'>
        {/* Password Input */}
        <Input
          icon={Lock}
          type="password"
          placeholder="Enter your password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (errors.password) setErrors({ ...errors, password: '' });
          }}
          error={errors.password}
          showPasswordToggle={true}
          autoComplete="current-password"
          disabled={loading}
          aria-label="Password"
        />
</div>
        {/* Remember Me & Forgot Password */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          fontSize: '13px'
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              style={{ cursor: 'pointer' }}
              aria-label="Remember me"
            />
            Remember me
          </label>
          <button
            type="button"
            onClick={() => setView('forgot')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--primary)',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            aria-label="Forgot your password?"
          >
            Forgot password?
          </button>
        </div>

        {/* Cloudflare Widget */}
        {/* <CloudflareWidget /> */}

        {/* Primary Action Button */}
        <Button
          type="submit"
          isLoading={loading}
          disabled={loading}
        >
          Sign In
        </Button>
      </form>

      {/* Helper Links */}
      <div className="terms-text" style={{
        marginTop: '20px',
        textAlign: 'center',
        fontSize: '13px',
        color: '#888',
        borderTop: '1px solid #333',
        paddingTop: '20px'
      }}>
        {/* <p style={{ margin: '0 0 8px 0' }}>
          Don't have an account?
          <button
            onClick={() => setView('signup')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--primary)',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Sign up
          </button>
        </p> */}


        <p className='footer' style={{ margin: '8px 0', fontSize: '11px' }}>
          By signing in, you agree to our{' '}
          <a href="#" onClick={(e) => handlePolicyClick(e, 'terms')}>
            Terms
          </a>
          {' '}and{' '}
          <a href="#" onClick={(e) => handlePolicyClick(e, 'privacy')}>
            Privacy Policy
          </a>
          {' '}and{' '}
          <a href="#" onClick={(e) => handlePolicyClick(e, 'cookie')}>
            Cookie&nbsp;Policy
          </a>
        </p>
      </div>
    </motion.div>
  );
};

// --- SIGN UP ---
export const SignUpForm = ({ setView }) => {
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    organization: '',
    country: null,
    state: null,
    city: null,
    countryCode: '',
    phone: '',
    siteRequest: null,
    roleRequest: null,
    purposeRequest: null,
    email: '',
    termsConditions: false
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState(null);

  // Policy Modal State
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyType, setPolicyType] = useState(null); // "terms", "privacy", "cookie"

  const handlePolicyClick = (e, type) => {
    e.preventDefault();
    setPolicyType(type);
    setShowPolicyModal(true);
  };

  // Data from APIs
  const [countriesData, setCountriesData] = useState([]);
  const [statesData, setStatesData] = useState([]);
  const [citiesData, setCitiesData] = useState([]);
  const [siteRequestsData, setSiteRequestsData] = useState([]);
  const [rolesData, setRolesData] = useState([]);
  const [purposesData, setPurposesData] = useState([]);

  // Loading states for dropdowns
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [statesLoading, setStatesLoading] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [staticDataLoading, setStaticDataLoading] = useState(true);

  // Fetch countries on component mount
  React.useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setCountriesLoading(true);
        const countries = await locationService.getCountries();
        setCountriesData(countries);
        console.log('Countries fetched:', countries);

        // Fetch static data (site requests, roles, purposes)
        const [sites, roles, purposes] = await Promise.all([
          locationService.getSiteRequests(),
          locationService.getRoles(),
          locationService.getPurposes()
        ]);

        setSiteRequestsData(sites);
        setRolesData(roles);
        setPurposesData(purposes);
        console.log('Static data fetched:', { sites, roles, purposes });
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setAlert({
          type: 'error',
          message: 'Failed to load form data. Please refresh the page.'
        });
      } finally {
        setCountriesLoading(false);
        setStaticDataLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Get available states based on country selection
  const availableStates = statesData;
  const availableCities = citiesData;

  // Update country code when country changes
  const handleCountryChange = async (country) => {
    console.log('Country selected:', country);
    setFormData({
      ...formData,
      country,
      countryCode: country.phonecode || '',
      state: null,
      city: null
    });

    // Fetch states for selected country
    try {
      setStatesLoading(true);
      const states = await locationService.getStates(country.id);
      setStatesData(states);
      setCitiesData([]); // Clear cities when country changes
      console.log('States fetched for country:', states);
    } catch (error) {
      console.error('Error fetching states:', error);
      setAlert({
        type: 'error',
        message: error.message || 'Failed to fetch states'
      });
    } finally {
      setStatesLoading(false);
    }
  };

  // Handle state selection and fetch cities
  const handleStateChange = async (state) => {
    console.log('State selected:', state);
    setFormData({ ...formData, state, city: null });

    if (!formData.country?.id || !state?.id) {
      console.log('Missing country or state ID');
      return;
    }

    // Fetch cities for selected state
    try {
      setCitiesLoading(true);
      const cities = await locationService.getCities(formData.country.id, state.id);
      setCitiesData(cities);
      console.log('Cities fetched for state:', cities);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setAlert({
        type: 'error',
        message: error.message || 'Failed to fetch cities'
      });
    } finally {
      setCitiesLoading(false);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstname.trim()) {
      newErrors.firstname = 'First name is required';
    }

    if (!formData.lastname.trim()) {
      newErrors.lastname = 'Last name is required';
    }

    if (!formData.organization.trim()) {
      newErrors.organization = 'Organization is required';
    }

    if (!formData.country) {
      newErrors.country = 'Country is required';
    }

    if (!formData.state) {
      newErrors.state = 'State is required';
    }

    if (!formData.city) {
      newErrors.city = 'City is required';
    }

    if (!formData.siteRequest) {
      newErrors.siteRequest = 'Site requested is required';
    }

    if (!formData.roleRequest) {
      newErrors.roleRequest = 'Role requested is required';
    }

    if (!formData.purposeRequest) {
      newErrors.purposeRequest = 'Purpose is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else if (!/^\d{7,15}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.termsConditions) {
      newErrors.termsConditions = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }

    setLoading(true);
    try {
      // Validate that all selections are made and have IDs
      if (!formData.country?.id) {
        throw new Error('Country ID is missing');
      }
      if (!formData.state?.id) {
        throw new Error('State ID is missing');
      }
      if (!formData.city?.id) {
        throw new Error('City ID is missing');
      }
      if (!formData.siteRequest?.id) {
        throw new Error('Site Request ID is missing');
      }
      if (!formData.roleRequest?.id) {
        throw new Error('Role Request ID is missing');
      }
      if (!formData.purposeRequest?.id) {
        throw new Error('Purpose Request ID is missing');
      }

      const signupData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        organization: formData.organization,
        country: formData.country.id,
        state: formData.state.id,
        city: formData.city.id,
        country_code: formData.countryCode,
        phone: formData.phone,
        site_request: formData.siteRequest.id,
        role_request: formData.roleRequest.id,
        purpose_request: formData.purposeRequest.id,
        email: formData.email,
        terms_conditions: formData.termsConditions
      };

      console.log('=== SENDING SIGNUP DATA ===');
      console.log('Full payload:', JSON.stringify(signupData, null, 2));
      const response = await authService.register(signupData);
      console.log('Signup response:', response);

      setAlert({
        type: 'success',
        message: 'Registration successful! Please check your email for activation link.'
      });
      setTimeout(() => {
        setView('success');
      }, 2000);
    } catch (error) {
      console.error('=== SIGNUP ERROR ===');
      console.error('Error object:', error);
      console.error('Error message:', error.message);
      console.error('Response status:', error.response?.status);
      console.error('Response data:', error.response?.data);

      let errorMessage = 'Registration failed. Please try again.';
      if (error.response?.data) {
        const errorData = error.response.data;
        console.log('Error data type:', typeof errorData);
        console.log('Error data content:', errorData);

        if (errorData.errors && typeof errorData.errors === 'object') {
          const errorMessages = Object.entries(errorData.errors)
            .map(([key, value]) => {
              if (Array.isArray(value)) {
                return `${key}: ${value.join(', ')}`;
              }
              return `${key}: ${value}`;
            })
            .filter(msg => msg)
            .join('\n');
          if (errorMessages) errorMessage = errorMessages;
        } else if (typeof errorData === 'object' && !Array.isArray(errorData)) {
          const errorMessages = Object.entries(errorData)
            .map(([key, value]) => {
              if (key === 'status' || key === 'errors') return null; // Skip status and nested errors if already handled
              if (Array.isArray(value)) {
                return `${key}: ${value.join(', ')}`;
              }
              return `${key}: ${value}`;
            })
            .filter(msg => msg)
            .join('\n');
          if (errorMessages) errorMessage = errorMessages;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else {
          errorMessage = JSON.stringify(errorData);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      console.error('Final error message to display:', errorMessage);

      setAlert({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  if (staticDataLoading) {
    return (
      <motion.div variants={anim} initial="hidden" animate="visible" exit="exit" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ color: '#888', fontSize: '14px' }}>Loading form data...</div>
      </motion.div>
    );
  }

  return (
    <motion.div variants={anim} initial="hidden" animate="visible" exit="exit">
      <PolicyModal
        isOpen={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        title={policyType ? POLICY_CONTENT[policyType].title : ""}
        content={policyType ? POLICY_CONTENT[policyType].content : null}
      />
      {alert && (
        <SystemAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <form onSubmit={handleSubmit}>
        {/* First Name & Last Name */}
         <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
          <Input
            icon={User}
            placeholder="First Name"
            value={formData.firstname}
            onChange={(e) => {
              setFormData({ ...formData, firstname: e.target.value });
              if (errors.firstname) setErrors({ ...errors, firstname: '' });
            }}
            error={errors.firstname}
            disabled={loading}
          />
          <Input
            icon={User}
            placeholder="Last Name"
            value={formData.lastname}
            onChange={(e) => {
              setFormData({ ...formData, lastname: e.target.value });
              if (errors.lastname) setErrors({ ...errors, lastname: '' });
            }}
            error={errors.lastname}
            disabled={loading}
          />
        </div>

        {/* Country & State */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
          <Autocomplete
            options={countriesData}
            value={formData.country}
            onChange={(country) => {
              handleCountryChange(country);
              if (errors.country) setErrors({ ...errors, country: '' });
            }}
            onInputChange={() => { }}
            placeholder="Country - Start typing to select"
            getOptionLabel={(opt) => opt.name}
            getOptionValue={(opt) => opt.id}
            error={errors.country}
            disabled={loading}
          />
          <Autocomplete
            options={availableStates}
            value={formData.state}
            onChange={(state) => {
              handleStateChange(state);
              if (errors.state) setErrors({ ...errors, state: '' });
            }}
            onInputChange={() => { }}
            placeholder="State - Start typing to select"
            getOptionLabel={(opt) => opt.name}
            getOptionValue={(opt) => opt.id}
            error={errors.state}
            disabled={loading || statesLoading || !formData.country}
          />
        </div>

        {/* City & Site Requested */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
          <Autocomplete
            options={availableCities}
            value={formData.city}
            onChange={(city) => {
              setFormData({ ...formData, city });
              if (errors.city) setErrors({ ...errors, city: '' });
            }}
            onInputChange={() => { }}
            placeholder="City - Start typing to select"
            getOptionLabel={(opt) => opt.name}
            getOptionValue={(opt) => opt.id}
            error={errors.city}
            disabled={loading || citiesLoading || !formData.state}
          />
          <Dropdown
            options={siteRequestsData}
            value={formData.siteRequest}
            onChange={(option) => {
              setFormData({ ...formData, siteRequest: option });
              if (errors.siteRequest) setErrors({ ...errors, siteRequest: '' });
            }}
            placeholder="Site Requested"
            getOptionLabel={(opt) => opt.name}
            getOptionValue={(opt) => opt.id}
            error={errors.siteRequest}
            disabled={loading}
          />
        </div>

        {/* Role Requested & Purpose */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
          <Dropdown
            options={rolesData}
            value={formData.roleRequest}
            onChange={(option) => {
              setFormData({ ...formData, roleRequest: option });
              if (errors.roleRequest) setErrors({ ...errors, roleRequest: '' });
            }}
            placeholder="Role Requested"
            getOptionLabel={(opt) => opt.name}
            getOptionValue={(opt) => opt.id}
            error={errors.roleRequest}
            disabled={loading}
          />
          <Dropdown
            options={purposesData}
            value={formData.purposeRequest}
            onChange={(option) => {
              setFormData({ ...formData, purposeRequest: option });
              if (errors.purposeRequest) setErrors({ ...errors, purposeRequest: '' });
            }}
            placeholder="Purpose"
            getOptionLabel={(opt) => opt.name}
            getOptionValue={(opt) => opt.id}
            error={errors.purposeRequest}
            disabled={loading}
          />
        </div>

        {/* Email & Organization */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
          <Input
            icon={Mail}
            type="email"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => {
              setFormData({ ...formData, email: e.target.value });
              if (errors.email) setErrors({ ...errors, email: '' });
            }}
            error={errors.email}
            disabled={loading}
            autoComplete="email"
          />
          <Input
            icon={Building}
            placeholder="Organization"
            value={formData.organization}
            onChange={(e) => {
              setFormData({ ...formData, organization: e.target.value });
              if (errors.organization) setErrors({ ...errors, organization: '' });
            }}
            error={errors.organization}
            disabled={loading}
          />
        </div>

        {/* Country Code & Phone */}
        <div className='country-sec'>
          <div className='countrycode_value' >
            +{formData.countryCode}
          </div>
          <Input
            icon={Phone}
            type="tel"
            placeholder="Phone"
            value={formData.phone}
            onChange={(e) => {
              setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') });
              if (errors.phone) setErrors({ ...errors, phone: '' });
            }}
            error={errors.phone}
            disabled={loading || !formData.countryCode}
          />
        </div>

        {/* Terms & Conditions */}
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#ccc',
          fontSize: '13px',
          marginBottom: '15px',
          cursor: 'pointer'
        }}>
          <input
            type="checkbox"
            checked={formData.termsConditions}
            onChange={(e) => {
              setFormData({ ...formData, termsConditions: e.target.checked });
              if (errors.termsConditions) setErrors({ ...errors, termsConditions: '' });
            }}
            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
            disabled={loading}
          />
          <span>
            I agree to the{' '}
            <a href="#" onClick={(e) => handlePolicyClick(e, 'terms')} style={{ color: 'var(--primary)', textDecoration: 'none' }}>
              Terms and Conditions
            </a>
          </span>
        </label>
        {errors.termsConditions && (
          <span style={{ display: 'block', color: '#ef4444', fontSize: '12px', marginTop: '-10px', marginBottom: '15px' }}>
            {errors.termsConditions}
          </span>
        )}

        {/* Cloudflare Widget */}
        {/* <CloudflareWidget /> */}

        {/* Submit Button */}
        <Button
          type="submit"
          isLoading={loading}
          disabled={loading}
        >
          {loading ? 'Creating account...' : 'Sign Up'}
        </Button>
      </form>

      {/* Helper Links */}
      <div className="terms-text" style={{
        marginTop: '20px',
        textAlign: 'center',
        fontSize: '13px',
        color: '#888',
        borderTop: '1px solid #333',
        paddingTop: '20px'
      }}>
        <p style={{ margin: '8px 0', fontSize: '11px' }}>
          Already have an account?{' '}
          <button
            onClick={() => setView('signin')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--primary)',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            Sign in
          </button>
        </p>
      </div>
    </motion.div>
  );
};

// --- OTP ---
export const OTPForm = ({ setView, email = '' }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState(null);
  const [resendTimer, setResendTimer] = useState(300); // 5 minutes in seconds

  // Countdown timer for resend OTP
  React.useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const handleOtpChange = (value, index) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only the last digit
    setOtp(newOtp);

    // Auto-focus to next input
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }

    // Clear errors when user starts typing
    if (errors.otp) {
      setErrors({ ...errors, otp: '' });
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const validateOtp = () => {
    const otpValue = otp.join('');
    const newErrors = {};

    if (otpValue.length !== 6) {
      newErrors.otp = 'Please enter all 6 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleVerify = async (e) => {
    e?.preventDefault?.();

    if (!validateOtp()) return;

    setLoading(true);
    try {
      const otpValue = otp.join('');
      console.log('=== OTP VERIFICATION ===');
      console.log('Email:', email);
      console.log('OTP:', otpValue);

      const response = await authService.verifyOtp(email, otpValue);
      console.log('OTP verification response:', response);

      // Store user data
      if (response.user) {
        localStorage.setItem('user', JSON.stringify(response.user));
        console.log('User data stored in localStorage:', response.user);
      }

      setAlert({ type: 'success', message: response.message || 'OTP verified successfully!' });

      // Use setTimeout to ensure state updates are processed before view change
      setTimeout(() => {
        console.log('Navigating to dashboard...');
        setView('dashboard');
      }, 800);
    } catch (error) {
      console.error('=== OTP VERIFICATION ERROR ===');
      console.error('Error:', error);
      console.error('Error response:', error.response?.data);

      let errorMessage = 'OTP verification failed';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setAlert({
        type: 'error',
        message: errorMessage
      });
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      // Call login endpoint to resend OTP
      await mockAuthService.login(email, '');
      setAlert({ type: 'success', message: 'OTP resent to your email' });
      setOtp(['', '', '', '', '', '']);
      setResendTimer(300);
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'Failed to resend OTP'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div variants={anim} initial="hidden" animate="visible" exit="exit">
      <h2 style={{ color: 'white', marginBottom: '8px' }}>Verify OTP</h2>
      <p style={{ color: '#888', fontSize: '14px', marginBottom: '20px' }}>
        Enter the OTP sent to {email}
      </p>

      {alert && (
        <SystemAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <form onSubmit={handleVerify}>
        {/* OTP Input Fields */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginBottom: '20px',
          justifyContent: 'center'
        }}>
          {otp.map((digit, index) => (
            <input
              key={index}
              id={`otp-${index}`}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleOtpChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              style={{
                width: '50px',
                height: '50px',
                fontSize: '20px',
                textAlign: 'center',
                fontWeight: 'bold',
                background: '#1a1a1a',
                border: `1px solid ${errors.otp ? '#ef4444' : '#333'}`,
                color: 'white',
                borderRadius: '4px',
                outline: 'none',
                transition: 'border-color 0.3s',
                cursor: loading ? 'not-allowed' : 'text'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--primary, #3b82f6)'}
              onBlur={(e) => e.target.style.borderColor = errors.otp ? '#ef4444' : '#333'}
              disabled={loading}
            />
          ))}
        </div>

        {errors.otp && (
          <span style={{
            display: 'block',
            color: '#ef4444',
            fontSize: '12px',
            marginBottom: '15px',
            textAlign: 'center'
          }}>
            {errors.otp}
          </span>
        )}

        {/* Cloudflare Widget */}
        {/* <CloudflareWidget isVerified={true} /> */}

        {/* Resend OTP Info */}
        <div style={{
          background: 'rgba(59, 130, 246, 0.1)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '15px',
          textAlign: 'center'
        }}>
          <p style={{ color: '#888', fontSize: '13px', margin: '0 0 8px 0' }}>
            Resend OTP in <span style={{ color: 'var(--primary)', fontWeight: '500' }}>
              {formatTime(resendTimer)}
            </span>
          </p>
          {resendTimer === 0 && (
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={loading}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--primary)',
                cursor: loading ? 'not-allowed' : 'pointer',
                textDecoration: 'underline',
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              Resend OTP
            </button>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          isLoading={loading}
          disabled={loading || otp.join('').length !== 6}
        >
          {loading ? 'Verifying...' : 'Verify OTP'}
        </Button>
      </form>

      {/* Helper Links */}
      <div style={{
        marginTop: '20px',
        textAlign: 'center',
        fontSize: '13px'
      }}>
        <button
          onClick={() => setView('signin')}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--primary)',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          Back to Sign In
        </button>
      </div>
    </motion.div>
  );
};

// --- UPDATE PASSWORD (ACCOUNT ACTIVATION) ---
export const UpdatePasswordForm = ({ setView, token = '' }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [alert, setAlert] = useState(null);

  // Verify token on component mount
  React.useEffect(() => {
    const verifyToken = async () => {
      try {
        console.log('Verifying activation token:', token);
        const response = await authService.verifyActivationToken(token);
        console.log('Token verification response:', response);
        setEmail(response.email || '');
        setAlert({
          type: 'success',
          message: response.message || 'Account approved. Please set your password.'
        });
      } catch (error) {
        console.error('Token verification failed:', error);
        setAlert({
          type: 'error',
          message: error.message || 'Invalid or expired activation link'
        });
      } finally {
        setTokenLoading(false);
      }
    };

    if (token) {
      verifyToken();
    }
  }, [token]);

  // Validate password in real-time
  const handlePasswordChange = (newPassword) => {
    setPassword(newPassword);

    // Clear password validation errors in main errors object
    if (errors.password) {
      setErrors({ ...errors, password: '' });
    }

    // Validate and show detailed errors
    const validationErrors = validatePassword(newPassword);
    setPasswordErrors(validationErrors);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Check password validation
    const passwordValidationErrors = validatePassword(password);
    if (passwordValidationErrors.length > 0) {
      newErrors.password = passwordValidationErrors.join('\n');
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      console.log('=== ACTIVATING ACCOUNT ===');
      console.log('Token:', token);
      console.log('Email:', email);

      const response = await authService.activateAccount(token, password, confirmPassword);
      console.log('Account activation response:', response);

      setAlert({
        type: 'success',
        message: 'Password updated successfully! Redirecting to login...'
      });

      setTimeout(() => {
        setView('signin');
      }, 2000);
    } catch (error) {
      console.error('=== ACCOUNT ACTIVATION ERROR ===');
      console.error('Error:', error);
      console.error('Error response:', error.response?.data);

      let errorMessage = 'Failed to update password. Please try again.';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object' && !Array.isArray(errorData)) {
          const messages = Object.entries(errorData)
            .map(([key, value]) => {
              if (Array.isArray(value)) {
                return `${key}: ${value.join(', ')}`;
              }
              return `${key}: ${value}`;
            })
            .filter(msg => msg)
            .join('\n');
          if (messages) errorMessage = messages;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setAlert({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  if (tokenLoading) {
    return (
      <motion.div variants={anim} initial="hidden" animate="visible" exit="exit" style={{ textAlign: 'center', padding: '40px 20px' }}>
        <div style={{ color: '#888', fontSize: '14px' }}>Verifying activation link...</div>
      </motion.div>
    );
  }

  return (
    <motion.div variants={anim} initial="hidden" animate="visible" exit="exit">
      {alert && (
        <SystemAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <form onSubmit={handleSubmit}>
        <h2 style={{ color: 'white', marginBottom: '8px', marginTop: 0 }}>Update Password</h2>
        {email && (
          <p style={{ color: '#888', fontSize: '13px', marginBottom: '20px' }}>
            Account: <strong>{email}</strong>
          </p>
        )}

        {/* Password Instructions */}

        {/* New Password */}
        <div className='mb-15'>
        <Input
          icon={Lock}
          type="password"
          placeholder="New Password"
          value={password}
          onChange={(e) => handlePasswordChange(e.target.value)}
          error={errors.password}
          showPasswordToggle={true}
          disabled={loading}
          style={{ marginBottom: '4px' }}
        /></div>
        
        <div style={{
          background: 'rgba(59, 130, 246, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '6px',
          padding: '12px 16px',
          marginBottom: '20px',
          fontSize: '12px'
        }}>
          <p style={{ margin: '0 0 8px 0', color: '#3b82f6', fontWeight: '500' }}>Password Requirements:</p>
          <ul style={{
            margin: '0',
            paddingLeft: '20px',
            color: passwordErrors.length === 0 ? '#22c55e' : '#888'
          }}>
            <li style={{
              marginBottom: '6px',
              color: !passwordErrors.some(e => e.includes('8 characters')) ? '#22c55e' : '#ef4444'
            }}>
              Must be at least 8 characters long
            </li>
            <li style={{
              marginBottom: '6px',
              color: !passwordErrors.some(e => e.includes('uppercase')) ? '#22c55e' : '#ef4444'
            }}>
              Must contain an uppercase letter (A-Z)
            </li>
            <li style={{
              marginBottom: '6px',
              color: !passwordErrors.some(e => e.includes('lowercase')) ? '#22c55e' : '#ef4444'
            }}>
              Must contain a lowercase letter (a-z)
            </li>
            <li style={{
              marginBottom: '6px',
              color: !passwordErrors.some(e => e.includes('number')) ? '#22c55e' : '#ef4444'
            }}>
              Must contain a number (0-9)
            </li>
            <li style={{
              color: !passwordErrors.some(e => e.includes('special character')) ? '#22c55e' : '#ef4444'
            }}>
              Must contain a special character (!, %, @, #, etc.)
            </li>
          </ul>
        </div>
 <div className='mb-15'>
        {/* Confirm Password */}
        <Input
          icon={Lock}
          type="password"
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
          }}
          error={errors.confirmPassword}
          showPasswordToggle={true}
          disabled={loading}
          style={{ marginBottom: '4px' }}
        />
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          isLoading={loading}
          disabled={loading || passwordErrors.length > 0}
        >
          {loading ? 'Updating Password...' : 'Update Password'}
        </Button>
      </form>

      {/* Helper Links */}
      <div style={{
        marginTop: '20px',
        textAlign: 'center',
        fontSize: '13px'
      }}>
        <button
          onClick={() => setView('signin')}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--primary)',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          Back to Sign In
        </button>
      </div>
    </motion.div>
  );
};

// --- FORGOT PASSWORD ---
export const ForgotPasswordForm = ({ setView }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState(null);

  const validateForm = () => {
    const newErrors = {};

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      console.log('=== FORGOT PASSWORD ===');
      console.log('Email:', email);

      const response = await authService.forgotPassword(email);
      console.log('Forgot password response:', response);

      setAlert({
        type: 'success',
        message: response.message || 'Password reset link sent to your email. Please check your inbox.'
      });

      setTimeout(() => {
        setEmail('');
        setView('signin');
      }, 3000);
    } catch (error) {
      console.error('=== FORGOT PASSWORD ERROR ===');
      console.error('Error:', error);
      console.error('Error response:', error.response?.data);

      let errorMessage = 'Failed to send reset link. Please try again.';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setAlert({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div variants={anim} initial="hidden" animate="visible" exit="exit">
      {alert && (
        <SystemAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <form onSubmit={handleSubmit}>
        <h2 style={{ color: 'white', marginBottom: '8px' }}>Forgot Password</h2>
        <p style={{ color: '#888', fontSize: '14px', marginBottom: '20px' }}>
          Enter your email address and we'll send you a link to reset your password.
        </p>

        {/* Email Input */}
        <Input
          icon={Mail}
          type="email"
          placeholder="Enter your email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errors.email) setErrors({ ...errors, email: '' });
          }}
          error={errors.email}
          autoComplete="username"
          disabled={loading}
          aria-label="Email address"
          style={{ marginBottom: '20px' }}
        />

        {/* Cloudflare Widget */}
        {/* <CloudflareWidget /> */}

        {/* Submit Button */}
        <Button
          type="submit"
          isLoading={loading}
          disabled={loading}
        >
          {loading ? 'Sending link...' : 'Send Reset Link'}
        </Button>
      </form>

      {/* Helper Links */}
      <div style={{
        marginTop: '20px',
        textAlign: 'center',
        fontSize: '13px'
      }}>
        <button
          onClick={() => setView('signin')}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--primary)',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          Back to Sign In
        </button>
      </div>
    </motion.div>
  );
};

// --- RESET PASSWORD ---
export const ResetPasswordForm = ({ setView, uid = '', token = '' }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [alert, setAlert] = useState(null);

  // Validate password in real-time
  const handlePasswordChange = (newPassword) => {
    setPassword(newPassword);

    if (errors.password) {
      setErrors({ ...errors, password: '' });
    }

    const validationErrors = validatePassword(newPassword);
    setPasswordErrors(validationErrors);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    const passwordValidationErrors = validatePassword(password);
    if (passwordValidationErrors.length > 0) {
      newErrors.password = passwordValidationErrors.join('\n');
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Confirm password is required';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {
      console.log('=== RESET PASSWORD ===');
      console.log('UID:', uid);
      console.log('Token:', token);

      const response = await authService.resetPassword(uid, token, password, confirmPassword);
      console.log('Reset password response:', response);

      setAlert({
        type: 'success',
        message: 'Password reset successfully! Redirecting to login...'
      });

      setTimeout(() => {
        setView('signin');
      }, 2000);
    } catch (error) {
      console.error('=== RESET PASSWORD ERROR ===');
      console.error('Error:', error);
      console.error('Error response:', error.response?.data);

      let errorMessage = 'Failed to reset password. Please try again.';
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object' && !Array.isArray(errorData)) {
          const messages = Object.entries(errorData)
            .map(([key, value]) => {
              if (Array.isArray(value)) {
                return `${key}: ${value.join(', ')}`;
              }
              return `${key}: ${value}`;
            })
            .filter(msg => msg)
            .join('\n');
          if (messages) errorMessage = messages;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setAlert({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div variants={anim} initial="hidden" animate="visible" exit="exit">
      {alert && (
        <SystemAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <form onSubmit={handleSubmit}>
        <h2 style={{ color: 'white', marginBottom: '8px', marginTop: 0 }}>Reset Password</h2>



        {/* New Password */}
        <Input
          icon={Lock}
          type="password"
          placeholder="New Password"
          value={password}
          onChange={(e) => handlePasswordChange(e.target.value)}
          error={errors.password}
          showPasswordToggle={true}
          disabled={loading}
          style={{ marginBottom: '15px' }}
        />

        {/* Password Instructions */}
        <div style={{
          background: 'rgba(59, 130, 246, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '6px',
          padding: '12px 16px',
          marginTop: '0px',
          marginBottom: '26px',
          fontSize: '12px'
        }}>
          <p style={{ margin: '0 0 8px 0', color: '#3b82f6', fontWeight: '500' }}>Password Requirements:</p>
          <ul style={{
            margin: '0',
            paddingLeft: '20px',
            color: passwordErrors.length === 0 ? '#22c55e' : '#888'
          }}>
            <li style={{
              marginBottom: '6px',
              color: !passwordErrors.some(e => e.includes('8 characters')) ? '#22c55e' : '#ef4444'
            }}>
              Must be at least 8 characters long
            </li>
            <li style={{
              marginBottom: '6px',
              color: !passwordErrors.some(e => e.includes('uppercase')) ? '#22c55e' : '#ef4444'
            }}>
              Must contain an uppercase letter (A-Z)
            </li>
            <li style={{
              marginBottom: '6px',
              color: !passwordErrors.some(e => e.includes('lowercase')) ? '#22c55e' : '#ef4444'
            }}>
              Must contain a lowercase letter (a-z)
            </li>
            <li style={{
              marginBottom: '6px',
              color: !passwordErrors.some(e => e.includes('number')) ? '#22c55e' : '#ef4444'
            }}>
              Must contain a number (0-9)
            </li>
            <li style={{
              color: !passwordErrors.some(e => e.includes('special character')) ? '#22c55e' : '#ef4444'
            }}>
              Must contain a special character (!, %, @, #, etc.)
            </li>
          </ul>
        </div>

        {/* Confirm Password */}
        <Input
          icon={Lock}
          type="password"
          placeholder="Confirm Password"
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: '' });
          }}
          error={errors.confirmPassword}
          showPasswordToggle={true}
          disabled={loading}
          style={{ marginBottom: '20px' }}
        />

        {/* Submit Button */}
        <Button
          type="submit"
          isLoading={loading}
          disabled={loading || passwordErrors.length > 0}
        >
          {loading ? 'Resetting Password...' : 'Reset Password'}
        </Button>
      </form>

      {/* Helper Links */}
      <div style={{
        marginTop: '20px',
        textAlign: 'center',
        fontSize: '13px'
      }}>
        <button
          onClick={() => setView('signin')}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--primary)',
            cursor: 'pointer',
            textDecoration: 'underline'
          }}
        >
          Back to Sign In
        </button>
      </div>
    </motion.div>
  );
};

// --- SUCCESS ---
export const SuccessView = ({ setView }) => (
  <motion.div variants={anim} initial="hidden" animate="visible" exit="exit" style={{ textAlign: 'center', padding: '20px' }}>
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}><CheckCircle size={50} color="#22c55e" /></div>
    <h2 style={{ color: 'white', margin: 0 }}>Success!</h2>
    <p style={{ color: '#888', fontSize: '14px', marginBottom: '20px' }}>Pending admin approval.</p>
    <Button variant="ghost" onClick={() => setView('signin')}>Back to Login</Button>
  </motion.div>
);